"use client";

import React from "react";
import { ArrowUpRight, Mail, Phone } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu, type MenuItem } from "@/integrations/wordpress/WordPressMenuProvider";
function BrandMark({
  size = 48,
  className = ""
}: {
  size?: number;
  className?: string;
}) {
  return <svg width={size} height={size} viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg" className={`shrink-0 ${className}`} aria-label="Decatur Heritage Renovations logo" role="img">
      <rect width="48" height="48" rx="8" fill="currentColor" className="text-primary/10" />
      <path d="M24 8L10 19V38C10 39.1046 10.8954 40 12 40H36C37.1046 40 38 39.1046 38 38V19L24 8Z" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" className="text-foreground" />
      <path d="M18 40V24H30V40" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" className="text-primary" />
      <path d="M24 15V19M22 17H26" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="text-primary" />
    </svg>;
}
const fallbackServices = [{
  label: "Kitchen Remodeling",
  href: "/kitchen-remodeling"
}, {
  label: "Bathroom Remodeling",
  href: "/bathroom-remodeling"
}, {
  label: "Whole-Home Remodeling",
  href: "/whole-home"
}, {
  label: "Outdoor Living & Decks",
  href: "/outdoor-living"
}, {
  label: "Process & Planning",
  href: "/process-planning"
}];
const fallbackCompanyLinks = [{
  label: "Home",
  href: "/"
}, {
  label: "Projects",
  href: "/projects"
}, {
  label: "About Us",
  href: "/about-us"
}, {
  label: "Blog",
  href: "/blog"
}, {
  label: "Contact",
  href: "/contact"
}];
function RecursiveMenuItems({
  items,
  level = 0
}: {
  items: MenuItem[];
  level?: number;
}) {
  if (!items || items.length === 0) return null;
  return <ul className={level === 0 ? "space-y-3" : "pl-3 mt-2 space-y-2 border-l border-border"}>
      {items.map((item, i) => <li key={item.id || `${item.label}-${i}`} data-index={i}>
          <Link to={item.href || "#"} className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground transition-colors group">
            <span>{item.label}</span>
            <ArrowUpRight className="ml-1 size-3 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-[opacity,transform] duration-200 text-foreground" />
          </Link>
          {item.children && item.children.length > 0 && <RecursiveMenuItems items={item.children} level={level + 1} />}
        </li>)}
    </ul>;
}
function FooterNavigationConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="space-y-3">
        <div className="h-4 w-24 bg-muted animate-pulse rounded-md" />
        <div className="h-4 w-32 bg-muted animate-pulse rounded-md" />
        <div className="h-4 w-28 bg-muted animate-pulse rounded-md" />
        <div className="h-4 w-20 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  if (menuItems && menuItems.length > 0) {
    return <RecursiveMenuItems items={menuItems} />;
  }
  return <ul className="space-y-3">
      {fallbackCompanyLinks.map((link, i) => <li key={link.label} data-index={i}>
          <Link to={link.href} className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground transition-colors group">
            <span>{link.label}</span>
            <ArrowUpRight className="ml-1 size-3 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-[opacity,transform] duration-200 text-foreground" />
          </Link>
        </li>)}
    </ul>;
}
export default function Footer() {
  const prefersReducedMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: prefersReducedMotion ? 1 : 0,
      y: prefersReducedMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="footer" data-nav="light" className="bg-background text-foreground border-t border-border pt-16 pb-12 sm:pt-20 sm:pb-16">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.15
      }}>
          {/* Top Tier: 4-Column Architectural Grid */}
          <div className="grid grid-cols-1 gap-10 md:grid-cols-2 lg:grid-cols-12 lg:gap-8 pb-14 sm:pb-16">
            {/* Column 1: Brand & Service Area Summary */}
            <div className="lg:col-span-4 space-y-6">
              <div>
                <Link to="/" className="inline-flex items-center gap-3.5 focus-visible:outline-2 focus-visible:outline-ring rounded-md group">
                  <BrandMark size={56} />
                  <div className="flex flex-col">
                    <span className="font-serif text-xl font-semibold text-foreground tracking-tight leading-tight">
                      Decatur Heritage
                    </span>
                    <span className="text-[10px] font-bold tracking-[0.2em] text-primary uppercase leading-none mt-1">
                      RENOVATIONS
                    </span>
                  </div>
                </Link>
              </div>

              <p className="text-sm text-muted-foreground leading-relaxed max-w-sm">
                Full-service residential remodeling contractor serving Decatur, Georgia and neighboring communities with custom kitchens, bathrooms, additions, and whole-home renovations.
              </p>

              <div className="space-y-2">
                <p className="text-xs font-semibold tracking-wide uppercase text-muted-foreground">
                  Primary Service Area
                </p>
                <p className="text-sm text-foreground">
                  Decatur, GA (Oakhurst, Winnona Park, Great Lakes, Downtown) &amp; surrounding communities.
                </p>
              </div>
            </div>

            {/* Column 2: Remodeling Services */}
            <div className="lg:col-span-3 space-y-4">
              <h3 className="text-sm font-semibold tracking-wide uppercase text-foreground font-sans border-b border-border pb-2">
                Remodeling Services
              </h3>
              <ul className="space-y-3">
                {fallbackServices.map((service, i) => <li key={service.label} data-index={i}>
                    <Link to={service.href} className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground transition-colors group">
                      <span>{service.label}</span>
                      <ArrowUpRight className="ml-1 size-3 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-[opacity,transform] duration-200 text-foreground" />
                    </Link>
                  </li>)}
              </ul>
            </div>

            {/* Column 3: Navigation Menu via Provider */}
            <div className="lg:col-span-2 space-y-4">
              <h3 className="text-sm font-semibold tracking-wide uppercase text-foreground font-sans border-b border-border pb-2">
                Company &amp; Work
              </h3>
              <MenuProvider menu_id="47">
                <FooterNavigationConsumer />
              </MenuProvider>
            </div>

            {/* Column 4: Contact & Project Inquiries */}
            <div className="lg:col-span-3 space-y-4">
              <h3 className="text-sm font-semibold tracking-wide uppercase text-foreground font-sans border-b border-border pb-2">
                Project Inquiries
              </h3>

              <div className="space-y-3.5 text-sm">
                <div className="flex items-center gap-2.5">
                  <Phone className="size-4 shrink-0 text-foreground" />
                  <a href="tel:+14708479076" className="text-foreground hover:underline transition-colors">
                    {"(470) 847-9076"}
                  </a>
                </div>

                <div className="flex items-center gap-2.5">
                  <Mail className="size-4 shrink-0 text-foreground" />
                  <a href="mailto:serviceleadalerts@gmail.com" className="text-muted-foreground hover:text-foreground transition-colors break-all">
                    serviceleadalerts@gmail.com
                  </a>
                </div>
              </div>

              <div className="pt-3">
                <Button asChild size="default" className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-md">
                  <Link to="#">
                    Request Consultation
                  </Link>
                </Button>
              </div>
            </div>
          </div>

          <Separator className="bg-border" />

          {/* Bottom Tier: Copyright & Legal Bar */}
          <div className="pt-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-muted-foreground">
            <p>
              {"© "}{new Date().getFullYear()}{" Decatur Heritage Renovations. All rights reserved."}
            </p>

            <div className="flex items-center gap-6">
              <Link to="#" className="hover:text-foreground transition-colors">
                Privacy Policy
              </Link>
              <Link to="#" className="hover:text-foreground transition-colors">
                Terms of Service
              </Link>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}